/**
 * ResourcesSection Component
 *
 * Displays resource categories with "Coming Soon" states
 * Phase 1: Preview only, Phase 2+: Links to full resource pages
 *
 * Contract: specs/001-home-page-sections/contracts/ResourcesSection.ts
 */

import React from 'react'
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip,
} from '@mui/material'
import {
  Article,
  Science,
  AccountBalance,
  VolunteerActivism,
} from '@mui/icons-material'
import { ResourcesContent } from '@/data/types'

export interface ResourcesSectionProps {
  /**
   * Resources content (title, categories array)
   */
  content: ResourcesContent

  /**
   * Callback when user clicks available resource category
   * @param categoryId - ID of the clicked category
   */
  onCategoryClick?: (categoryId: string) => void

  /**
   * Optional CSS class name for styling
   */
  className?: string

  /**
   * Test ID for automated testing
   */
  testId?: string
}

/**
 * Map icon strings to MUI icon components
 */
const ICON_MAP: Record<string, React.ElementType> = {
  Article,
  Science,
  AccountBalance,
  VolunteerActivism,
}

/**
 * ResourcesSection Component
 */
export const ResourcesSection: React.FC<ResourcesSectionProps> = ({
  content,
  onCategoryClick,
  className,
  testId = 'resources-section',
}) => {
  const handleCategoryClick = (categoryId: string, isAvailable: boolean) => {
    if (isAvailable && onCategoryClick) {
      onCategoryClick(categoryId)
    }
  }

  return (
    <Box
      component="section"
      aria-labelledby="resources-title"
      className={className}
      data-testid={testId}
      sx={{
        py: { xs: 6, md: 8 },
        bgcolor: 'background.default',
      }}
    >
      <Container maxWidth="lg">
        {/* Section Title */}
        <Typography
          id="resources-title"
          variant="h2"
          component="h2"
          align="center"
          gutterBottom
          sx={{
            mb: 6,
            fontWeight: 700,
            fontSize: { xs: '2rem', md: '2.5rem' },
          }}
        >
          {content.title}
        </Typography>

        {/* Resource Categories Grid */}
        <Grid container spacing={4}>
          {content.categories.map(category => {
            const IconComponent = ICON_MAP[category.icon]
            const isAvailable = category.status === 'available'
            const isDisabled = category.status === 'coming-soon'

            return (
              <Grid item xs={12} sm={6} md={3} key={category.id}>
                <Card
                  component="article"
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    opacity: isDisabled ? 0.7 : 1,
                  }}
                >
                  <CardActionArea
                    onClick={() =>
                      handleCategoryClick(category.id, isAvailable)
                    }
                    disabled={isDisabled}
                    aria-disabled={isDisabled}
                    role="button"
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'stretch',
                      cursor: isDisabled ? 'default' : 'pointer',
                    }}
                  >
                    <CardContent
                      sx={{
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        p: 3,
                        textAlign: 'center',
                      }}
                    >
                      {/* Category Icon */}
                      {IconComponent && (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            mb: 2,
                          }}
                        >
                          <IconComponent
                            aria-hidden="true"
                            sx={{
                              fontSize: 56,
                              color: isDisabled
                                ? 'text.disabled'
                                : 'primary.main',
                            }}
                          />
                        </Box>
                      )}

                      {/* Category Title */}
                      <Typography
                        variant="h3"
                        component="h3"
                        gutterBottom
                        sx={{
                          fontWeight: 600,
                          fontSize: { xs: '1.25rem', md: '1.5rem' },
                          mb: 2,
                        }}
                      >
                        {category.title}
                      </Typography>

                      {/* Category Description */}
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 2,
                          flexGrow: 1,
                        }}
                      >
                        {category.description}
                      </Typography>

                      {/* Status Badge or Article Count */}
                      {isDisabled ? (
                        <Chip
                          label="Coming Soon"
                          size="small"
                          sx={{
                            mt: 'auto',
                            bgcolor: 'grey.200',
                            color: 'text.secondary',
                          }}
                        />
                      ) : category.articleCount ? (
                        <Chip
                          label={`${category.articleCount} Articles`}
                          size="small"
                          color="primary"
                          sx={{ mt: 'auto' }}
                        />
                      ) : null}
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            )
          })}
        </Grid>
      </Container>
    </Box>
  )
}

export default ResourcesSection
